import { useState, type FormEvent, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { auth } from '@/firebase'
import { AuthPageFrame } from '@/components/auth/AuthPageFrame'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phone-input'
import { textLinkClass } from '@/components/ui/text-link'
import { TwoStepMessages } from '@/constants/messages'
import { TWO_STEP_CHANGED } from '@/services/twoFactor'
import { useTextSetup } from '@/hooks/useTextSetup'
import { logOut } from '@/services/auth'
import { ConfirmIdentityDialog } from '@/components/profile/ConfirmIdentityDialog'

/**
 * Firebase adds a text step only to an account with a confirmed email.
 * `onInvitationAccepted` confirms it on the server right after acceptance,
 * without the browser waiting, so give it a few seconds to land.
 */
async function waitForConfirmedEmail(): Promise<void> {
  for (let i = 0; i < 10; i++) {
    const user = auth.currentUser
    if (!user) return
    await user.reload()
    if (user.emailVerified) {
      await user.getIdToken(true)
      return
    }
    await new Promise(r => setTimeout(r, 1000))
  }
  console.warn('[ConfirmPhone] email still not confirmed after 10s')
}

/**
 * Admins confirm a phone as their two-step sign-in method straight after
 * accepting the invitation (PB-88, D126/D129): phone → code → dashboard.
 * Also where `TwoStepGate` sends an admin who left before finishing.
 */
export default function ConfirmPhone(): ReactElement {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState<null | (() => Promise<void>)>(
    null
  )

  /** Firebase wants a recent sign-in to add a phone: confirm it's them first. */
  const withRecentSignIn = async (action: () => Promise<void>) => {
    try {
      await action()
    } catch (err) {
      if ((err as { code?: string })?.code === 'auth/requires-recent-login') {
        console.info(
          '[ConfirmPhone] recent sign-in needed, confirming identity'
        )
        setConfirming(() => action)
        return
      }
      throw err
    }
  }

  const text = useTextSetup({
    recaptchaId: 'confirm-phone-recaptcha',
    beforeSend: waitForConfirmedEmail,
    wrapSend: withRecentSignIn,
  })

  const send = async (e: FormEvent) => {
    e.preventDefault()
    await text.send()
  }

  const resend = async () => {
    if (await text.send()) toast.success(TwoStepMessages.phoneStepResent)
  }

  const finish = async (e: FormEvent) => {
    e.preventDefault()
    if (!(await text.finish())) return
    window.dispatchEvent(new Event(TWO_STEP_CHANGED))
    navigate('/dashboard', { replace: true })
  }

  return (
    <AuthPageFrame>
      <Card>
        <CardHeader>
          <CardTitle>{TwoStepMessages.phoneStepTitle}</CardTitle>
          <CardDescription>
            {text.codeSent
              ? TwoStepMessages.enterTextCode(text.phone)
              : TwoStepMessages.phoneStepText}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!text.codeSent ? (
            <form onSubmit={send} className="grid gap-4">
              <PhoneInput
                value={text.phone}
                onChange={text.setPhone}
                country={text.country}
                onCountryChange={iso => iso && text.setCountry(iso)}
              />
              <p className="text-xs text-muted-foreground">
                {TwoStepMessages.phoneStepRates}
              </p>
              {text.error && (
                <p className="text-sm text-destructive">{text.error}</p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={text.busy || !text.phone}
              >
                {text.busy ? TwoStepMessages.sending : TwoStepMessages.sendCode}
              </Button>
            </form>
          ) : (
            <form onSubmit={finish} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="confirm-phone-code">
                  {TwoStepMessages.codeLabel}
                </Label>
                <Input
                  id="confirm-phone-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={text.code}
                  onChange={e => text.setCode(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              {text.error && (
                <p className="text-sm text-destructive">{text.error}</p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={text.busy || text.code.length !== 6}
              >
                {text.busy
                  ? TwoStepMessages.checking
                  : TwoStepMessages.phoneStepDone}
              </Button>
              <p className="text-center text-sm">
                {TwoStepMessages.phoneStepResendPrompt}{' '}
                <button
                  type="button"
                  className={textLinkClass}
                  disabled={text.busy}
                  onClick={resend}
                >
                  {TwoStepMessages.phoneStepResend}
                </button>
              </p>
              <p className="text-center text-sm">
                <button
                  type="button"
                  className={textLinkClass}
                  disabled={text.busy}
                  onClick={text.changeNumber}
                >
                  {TwoStepMessages.phoneStepChangeNumber}
                </button>
              </p>
            </form>
          )}
          <div id="confirm-phone-recaptcha" />
        </CardContent>
      </Card>
      {/* A way out: sign in as someone else (user, 2026-11-01) */}
      <p className="text-center text-sm">
        <button
          type="button"
          className={textLinkClass}
          onClick={async () => {
            try {
              await logOut()
            } catch (err) {
              console.error('[ConfirmPhone] sign out failed', err)
            }
            navigate('/signin', { replace: true })
          }}
        >
          {TwoStepMessages.phoneStepSignOut}
        </button>
      </p>
      <ConfirmIdentityDialog
        action={confirming}
        onClose={() => setConfirming(null)}
      />
    </AuthPageFrame>
  )
}
